import React from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { CATEGORIES, TYPE_LABEL, MONTHS_IT } from "../lib/categories";
import { useSite } from "@/context/SiteData";

const Select = ({ value, onChange, children, testid }) => (
  <div className="relative">
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="appearance-none w-full sm:w-auto bg-white border border-gray-200 rounded-xl pl-4 pr-9 py-2.5 text-sm font-600 text-gray-700 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#EA6A0A]/20 focus:border-[#EA6A0A] transition-all cursor-pointer"
    >
      {children}
    </select>
    <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M2.5 4.5L6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
);

const MONTH_OPTIONS = [7, 8, 9, 10, 11, 12];
const DURATIONS = [
  { value: "short", label: "Fino a 4 ore" },
  { value: "mid", label: "5 - 8 ore" },
  { value: "long", label: "9 - 16 ore" },
  { value: "xl", label: "Oltre 16 ore" },
];

export const Filters = ({ filters, setFilters, locations, resultsCount }) => {
  const { cats } = useSite();
  const set = (key) => (val) => setFilters((f) => ({ ...f, [key]: val }));
  const hasActive =
    filters.search || filters.month !== "all" || filters.category !== "all" ||
    filters.duration !== "all" || filters.location !== "all" || filters.type !== "all";

  const reset = () =>
    setFilters({ search: "", month: "all", category: "all", duration: "all", location: "all", type: "all" });

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          data-testid="search-input"
          type="text"
          value={filters.search}
          onChange={(e) => set("search")(e.target.value)}
          placeholder="Cerca un corso... (es. Antincendio, Primo Soccorso, RSPP)"
          className="w-full bg-white border border-gray-200 rounded-2xl pl-12 pr-4 py-3.5 text-sm text-gray-800 placeholder:text-gray-400 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#EA6A0A]/20 focus:border-[#EA6A0A] transition-all shadow-[0_2px_10px_rgba(0,0,0,0.02)]"
        />
        {filters.search && (
          <button
            data-testid="search-clear"
            onClick={() => set("search")("")}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-600 text-gray-400 mr-1">
          <SlidersHorizontal size={14} /> Filtri
        </span>

        <Select testid="filter-month" value={filters.month} onChange={set("month")}>
          <option value="all">Tutti i mesi</option>
          {MONTH_OPTIONS.map((m) => (
            <option key={m} value={m}>{MONTHS_IT[m - 1]}</option>
          ))}
        </Select>

        <Select testid="filter-category" value={filters.category} onChange={set("category")}>
          <option value="all">Tutte le categorie</option>
          {cats.map((c) => (
            <option key={c.key || c.id} value={c.key || c.id}>{c.label}</option>
          ))}
        </Select>

        <Select testid="filter-duration" value={filters.duration} onChange={set("duration")}>
          <option value="all">Qualsiasi durata</option>
          {DURATIONS.map((d) => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </Select>

        <Select testid="filter-location" value={filters.location} onChange={set("location")}>
          <option value="all">Tutte le sedi</option>
          {locations.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </Select>

        <Select testid="filter-type" value={filters.type} onChange={set("type")}>
          <option value="all">Tipologia</option>
          <option value="full">{TYPE_LABEL.full}</option>
          <option value="refresh">{TYPE_LABEL.refresh}</option>
        </Select>

        {hasActive && (
          <button
            data-testid="filter-reset"
            onClick={reset}
            className="inline-flex items-center gap-1.5 text-sm font-600 text-[#EF4444] hover:underline px-2"
          >
            <X size={14} /> Azzera
          </button>
        )}

        <span data-testid="results-count" className="ml-auto text-sm text-gray-400 font-600">
          {resultsCount} {resultsCount === 1 ? "corso" : "corsi"}
        </span>
      </div>
    </div>
  );
};
